import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Bell, Shield, Trash2, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMutation, useQuery } from '@apollo/client';
import Button from '../components/ui/Button';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Dialog from '../components/ui/Dialog';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_SETTINGS } from '../graphql/queries/accountSettings';
import { UPDATE_USER_PROFILE } from '../graphql/mutations/updateUserProfile';
import { UPDATE_NOTIFICATION_SETTINGS } from '../graphql/mutations/updateNotificationSettings';
import { CHANGE_PASSWORD } from '../graphql/mutations/changePassword';
import { UPDATE_AVATAR_IMAGE } from '../graphql/mutations/updateAvatarImage';
import { DELETE_ACCOUNT } from '../graphql/mutations/deleteAccount';
import { UPDATE_EMAIL } from '../graphql/mutations/updateEmail';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [showDeleteAccountDialog, setShowDeleteAccountDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showAvatarCropDialog, setShowAvatarCropDialog] = useState(false);
  const [avatarDraftDataUrl, setAvatarDraftDataUrl] = useState<string | null>(null);
  const [avatarCacheBuster, setAvatarCacheBuster] = useState<number>(Date.now());
  const [avatarCrop, setAvatarCrop] = useState({
    zoom: 1,
    x: 0,
    y: 0,
  });
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    postalCode: '',
    about: '',
    avatarImageUrl: '',
  });
  const [notifications, setNotifications] = useState({
    email: true,
    push: true,
    marketing: false,
  });
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    next: '',
    confirm: '',
  });
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    currentPassword: '',
  });

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { loading: accountLoading, refetch } = useQuery(ACCOUNT_SETTINGS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const currentUser = data?.currentUser;
      if (!currentUser) return;

      setProfile({
        name: currentUser.name || '',
        email: currentUser.email || '',
        phoneNumber: currentUser.phoneNumber || '',
        address: currentUser.address || '',
        city: currentUser.city || '',
        postalCode: currentUser.postalCode || '',
        about: currentUser.about || '',
        avatarImageUrl: currentUser.avatarImageUrl || '',
      });

      setNotifications({
        email: currentUser.notificationSetting?.emailEnabled ?? true,
        push: currentUser.notificationSetting?.pushEnabled ?? true,
        marketing: currentUser.notificationSetting?.marketingEnabled ?? false,
      });
    },
    onError: (error) => {
      toast.error(`Kunne ikke hente kontoindstillinger: ${error.message}`);
    },
  });

  const [updateUserProfile, { loading: savingProfile }] = useMutation(UPDATE_USER_PROFILE);
  const [updateNotificationSettings, { loading: savingNotifications }] = useMutation(UPDATE_NOTIFICATION_SETTINGS);
  const [changePassword, { loading: changingPassword }] = useMutation(CHANGE_PASSWORD);
  const [updateAvatarImage, { loading: updatingAvatar }] = useMutation(UPDATE_AVATAR_IMAGE);
  const [deleteAccount, { loading: deletingAccount }] = useMutation(DELETE_ACCOUNT);
  const [updateEmail, { loading: updatingEmail }] = useMutation(UPDATE_EMAIL);
  
  const handleNotificationChange = (key: keyof typeof notifications) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getAvatarSrc = () => {
    const avatarSrc = profile.avatarImageUrl || user?.avatarImageUrl || user?.avatar || '';
    if (!avatarSrc) return '';
    const separator = avatarSrc.includes('?') ? '&' : '?';
    return `${avatarSrc}${separator}v=${avatarCacheBuster}`;
  };

  const handleLogout = () => {
    signOut();
    navigate('/');
  };

  const handleSaveProfile = async () => {
    try {
      const result = await updateUserProfile({
        variables: {
          name: profile.name || null,
          phoneNumber: profile.phoneNumber || null,
          address: profile.address || null,
          city: profile.city || null,
          postalCode: profile.postalCode || null,
          about: profile.about || null,
        },
      });

      const errors = result.data?.updateUserProfile?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors.join(', '));
        return;
      }

      toast.success('Profil opdateret');
      await refetch();
    } catch (error: any) {
      toast.error(error.message || 'Kunne ikke opdatere profil');
    }
  };

  const handleSaveNotifications = async () => {
    try {
      const result = await updateNotificationSettings({
        variables: {
          emailEnabled: notifications.email,
          pushEnabled: notifications.push,
          marketingEnabled: notifications.marketing,
        },
      });

      const errors = result.data?.updateNotificationSettings?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors.join(', '));
        return;
      }

      toast.success('Notifikationsindstillinger gemt');
      await refetch();
    } catch (error: any) {
      toast.error(error.message || 'Kunne ikke opdatere notifikationsindstillinger');
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordForm.current || !passwordForm.next || !passwordForm.confirm) {
      toast.error('Udfyld alle adgangskodefelter');
      return;
    }

    try {
      const result = await changePassword({
        variables: {
          current: passwordForm.current,
          next: passwordForm.next,
          confirm: passwordForm.confirm,
        },
      });

      const errors = result.data?.changePassword?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors.join(', '));
        return;
      }

      if (!result.data?.changePassword?.success) {
        toast.error('Kunne ikke ændre adgangskode');
        return;
      }

      toast.success('Adgangskode opdateret');
      setPasswordForm({ current: '', next: '', confirm: '' });
    } catch (error: any) {
      toast.error(error.message || 'Kunne ikke ændre adgangskode');
    }
  };

  const resetAvatarCropState = () => {
    setAvatarDraftDataUrl(null);
    setAvatarCrop({ zoom: 1, x: 0, y: 0 });
    setShowAvatarCropDialog(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const createCroppedAvatarBase64 = async (dataUrl: string, zoom: number, offsetX: number, offsetY: number): Promise<string> => {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Kunne ikke indlæse billedet'));
      img.src = dataUrl;
    });

    const size = 600;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Kunne ikke oprette billede-canvas');
    }

    const baseScale = Math.max(size / image.width, size / image.height);
    const totalScale = baseScale * zoom;
    const drawWidth = image.width * totalScale;
    const drawHeight = image.height * totalScale;
    const drawX = (size - drawWidth) / 2 + offsetX;
    const drawY = (size - drawHeight) / 2 + offsetY;

    context.clearRect(0, 0, size, size);
    context.drawImage(image, drawX, drawY, drawWidth, drawHeight);

    const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.92);
    return croppedDataUrl.split(',')[1];
  };

  const handleOpenEmailDialog = () => {
    setEmailForm({
      newEmail: profile.email,
      currentPassword: '',
    });
    setShowEmailDialog(true);
  };

  const handleUpdateEmail = async () => {
    if (!emailForm.newEmail.trim()) {
      toast.error('Indtast en ny e-mailadresse');
      return;
    }

    if (!emailForm.currentPassword.trim()) {
      toast.error('Indtast din nuværende adgangskode');
      return;
    }

    try {
      const result = await updateEmail({
        variables: {
          newEmail: emailForm.newEmail.trim(),
          currentPassword: emailForm.currentPassword,
        },
      });

      const errors = result.data?.updateEmail?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors.join(', '));
        return;
      }

      if (!result.data?.updateEmail?.success) {
        toast.error('Kunne ikke opdatere e-mail');
        return;
      }

      toast.success('E-mail opdateret');
      setShowEmailDialog(false);
      setEmailForm({ newEmail: '', currentPassword: '' });
      await refetch();
    } catch (error: any) {
      toast.error(error.message || 'Kunne ikke opdatere e-mail');
    }
  };

  const handleAvatarSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          if (!result) {
            reject(new Error('Kunne ikke læse filen'));
            return;
          }
          resolve(result);
        };
        reader.onerror = () => reject(new Error('Kunne ikke læse filen'));
        reader.readAsDataURL(file);
      });

      setAvatarDraftDataUrl(dataUrl);
      setAvatarCrop({ zoom: 1, x: 0, y: 0 });
      setShowAvatarCropDialog(true);
    } catch (error: any) {
      toast.error(error.message || 'Kunne ikke læse billedet');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleUploadCroppedAvatar = async () => {
    if (!avatarDraftDataUrl) return;

    try {
      const base64 = await createCroppedAvatarBase64(
        avatarDraftDataUrl,
        avatarCrop.zoom,
        avatarCrop.x,
        avatarCrop.y
      );

      const mutationResult = await updateAvatarImage({
        variables: {
          attachment: {
            filename: 'avatar.jpg',
            contentType: 'image/jpeg',
            base64,
          },
        },
      });

      const errors = mutationResult.data?.updateAvatarImage?.errors;
      if (errors && errors.length > 0) {
        toast.error(errors.join(', '));
        return;
      }

      const updatedAvatarUrl = mutationResult.data?.updateAvatarImage?.user?.avatarImageUrl;
      if (updatedAvatarUrl) {
        setProfile((prev) => ({ ...prev, avatarImageUrl: updatedAvatarUrl }));
      }
      setAvatarCacheBuster(Date.now());

      toast.success('Profilbillede opdateret');
      await refetch();
      resetAvatarCropState();
    } catch (error: any) {
      toast.error(error.message || 'Kunne ikke opdatere profilbillede');
    }
  };

  const handleDeleteAccountConfirm = async () => {
    try {
      const result = await deleteAccount();
      const errors = result.data?.deleteAccount?.errors;

      if (errors && errors.length > 0) {
        toast.error(errors.join(', '));
        return;
      }

      if (!result.data?.deleteAccount?.success) {
        toast.error('Kunne ikke slette konto');
        return;
      }

      toast.success('Konto slettet');
      setShowDeleteAccountDialog(false);
      signOut();
      navigate('/');
    } catch (error: any) {
      toast.error(error.message || 'Kunne ikke slette konto');
    }
  };

  if (accountLoading) {
    return (
      <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <Link to="/dashboard" className="flex items-center text-green-600 mb-4 hover:text-green-700">
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Tilbage til dashboard</span>
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Kontoindstillinger</h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <nav className="p-4">
              <div className="space-y-1">
                <a href="#profile" className="flex items-center px-3 py-2 text-sm font-medium rounded-md bg-green-50 text-green-700">
                  <User className="h-5 w-5 mr-3" />
                  Profiloplysninger
                </a>
                <a href="#notifications" className="flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50">
                  <Bell className="h-5 w-5 mr-3" />
                  Notifikationer
                </a>
                <a href="#security" className="flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50">
                  <Shield className="h-5 w-5 mr-3" />
                  Sikkerhed
                </a>
              </div>
            </nav>
          </div>
        </div>
        
        {/* Settings Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Profile Section */}
          <div id="profile" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">Profiloplysninger</h2>
              
              <div className="space-y-6">
                <div>
                  <label htmlFor="avatar" className="block text-sm font-medium text-gray-700 mb-2">
                    Profilbillede
                  </label>
                  <div className="flex items-center">
                    <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
                      {(profile.avatarImageUrl || user?.avatarImageUrl || user?.avatar) ? (
                        <img src={getAvatarSrc()} alt={profile.name || user?.name} className="h-full w-full object-cover" />
                      ) : (
                        <User className="h-8 w-8 text-gray-400" />
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleAvatarSelect}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-4"
                      onClick={() => fileInputRef.current?.click()}
                      isLoading={updatingAvatar}
                    >
                      Skift billede
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Fulde navn
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={profile.name}
                      onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        E-mailadresse
                      </label>
                      <Button variant="outline" size="sm" onClick={handleOpenEmailDialog}>
                        Skift e-mail
                      </Button>
                    </div>
                    <input
                      type="email"
                      id="email"
                      value={profile.email}
                      disabled
                      className="block w-full rounded-md border-gray-300 bg-gray-50 px-3 py-2 text-gray-500 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                      Telefonnummer
                    </label>
                    <input
                      type="text"
                      id="phoneNumber"
                      value={profile.phoneNumber}
                      onChange={(e) => setProfile(prev => ({ ...prev, phoneNumber: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
                      Adresse
                    </label>
                    <input
                      type="text"
                      id="address"
                      value={profile.address}
                      onChange={(e) => setProfile(prev => ({ ...prev, address: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
                      Postnummer
                    </label>
                    <input
                      type="text"
                      id="postalCode"
                      value={profile.postalCode}
                      onChange={(e) => setProfile(prev => ({ ...prev, postalCode: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                      By
                    </label>
                    <input
                      type="text"
                      id="city"
                      value={profile.city}
                      onChange={(e) => setProfile(prev => ({ ...prev, city: e.target.value }))}
                      className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-1">
                    Om dig
                  </label>
                  <textarea
                    id="bio"
                    rows={4}
                    value={profile.about}
                    onChange={(e) => setProfile(prev => ({ ...prev, about: e.target.value }))}
                    className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    placeholder="Fortæl andre lidt om dig selv..."
                  />
                </div>
                
                <div className="flex justify-end">
                  <Button variant="primary" onClick={handleSaveProfile} isLoading={savingProfile}>
                    Gem ændringer
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Notifications Section */}
          <div id="notifications" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">Notifikationsindstillinger</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">E-mailnotifikationer</h3>
                    <p className="text-sm text-gray-500">Modtag opdateringer om dine lejemål</p>
                  </div>
                  <button
                    onClick={() => handleNotificationChange('email')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      notifications.email ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className="sr-only">Slå e-mailnotifikationer til eller fra</span>
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        notifications.email ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Push-notifikationer</h3>
                    <p className="text-sm text-gray-500">Få opdateringer med det samme i din browser</p>
                  </div>
                  <button
                    onClick={() => handleNotificationChange('push')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      notifications.push ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className="sr-only">Slå push-notifikationer til eller fra</span>
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        notifications.push ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">Marketingmails</h3>
                    <p className="text-sm text-gray-500">Modtag nyheder og særlige tilbud</p>
                  </div>
                  <button
                    onClick={() => handleNotificationChange('marketing')}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      notifications.marketing ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span className="sr-only">Slå marketingmails til eller fra</span>
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        notifications.marketing ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex justify-end pt-2">
                  <Button variant="primary" onClick={handleSaveNotifications} isLoading={savingNotifications}>
                    Gem ændringer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        
          {/* Security Section */}
          <div id="security" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-6">Sikkerhedsindstillinger</h2>
              
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Skift adgangskode</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="current-password" className="block text-sm font-medium text-gray-700 mb-1">
                        Nuværende adgangskode
                      </label>
                      <input
                        type="password"
                        id="current-password"
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                        className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
                        Ny adgangskode
                      </label>
                      <input
                        type="password"
                        id="new-password"
                        value={passwordForm.next}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, next: e.target.value }))}
                        className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>
                    <div>
                      <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                        Bekræft ny adgangskode
                      </label>
                      <input
                        type="password"
                        id="confirm-password"
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                        className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      />
                    </div>
                    <div className="flex justify-end">
                      <Button variant="primary" onClick={handlePasswordChange} isLoading={changingPassword}>
                        Opdater adgangskode
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-900 mb-4">Tofaktorautentificering</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Tilføj et ekstra sikkerhedslag til din konto ved at aktivere tofaktorautentificering.
                  </p>
                  <Button variant="outline">Aktivér 2FA</Button>
                </div> */}
              </div>
            </div>
          </div>
          
          {/* Danger Zone */}
          <div className="bg-red-50 rounded-lg p-6">
            <h2 className="text-lg font-medium text-red-800 mb-4">Farezone</h2>
            <div className="space-y-4">
              <Button 
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100"
                icon={<Trash2 className="h-4 w-4 mr-2" />}
                onClick={() => setShowDeleteAccountDialog(true)}
              >
                Slet konto
              </Button>
              <Button 
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-100 ml-4"
                icon={<LogOut className="h-4 w-4 mr-2" />}
                onClick={handleLogout}
              >
                Log ud
              </Button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteAccountDialog}
        onClose={() => setShowDeleteAccountDialog(false)}
        onConfirm={handleDeleteAccountConfirm}
        title="Bekræft sletning af konto"
        description="Er du sikker på, at du vil slette din konto? Denne handling kan ikke fortrydes."
        requiredConfirmationText="Slet konto"
        confirmationFieldLabel={'Skriv "Slet konto" for at bekræfte'}
        confirmationFieldPlaceholder="Slet konto"
        confirmLabel="Slet konto"
        cancelLabel="Annuller"
        confirmButtonVariant="outline"
        isLoading={deletingAccount}
      />

      <Dialog
        isOpen={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        title="Skift e-mail"
        size="sm"
      >
        <div className="p-6 space-y-4">
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800">
            Advarsel: Når du ændrer e-mail, ændrer du også brugernavn til login.
          </div>

          <div>
            <label htmlFor="new-email" className="block text-sm font-medium text-gray-700 mb-1">
              Ny e-mailadresse
            </label>
            <input
              id="new-email"
              type="email"
              value={emailForm.newEmail}
              onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
              className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="current-password-email" className="block text-sm font-medium text-gray-700 mb-1">
              Nuværende adgangskode
            </label>
            <input
              id="current-password-email"
              type="password"
              value={emailForm.currentPassword}
              onChange={(e) => setEmailForm(prev => ({ ...prev, currentPassword: e.target.value }))}
              className="block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button variant="outline" onClick={() => setShowEmailDialog(false)} disabled={updatingEmail}>
              Annuller
            </Button>
            <Button variant="primary" onClick={handleUpdateEmail} isLoading={updatingEmail}>
              Opdater e-mail
            </Button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showAvatarCropDialog}
        onClose={resetAvatarCropState}
        title="Tilpas profilbillede"
        size="md"
      >
        <div className="p-6 space-y-5">
          <div className="flex justify-center">
            <div className="relative h-64 w-64 rounded-full overflow-hidden bg-gray-100 border border-gray-200">
              {avatarDraftDataUrl && (
                <img
                  src={avatarDraftDataUrl}
                  alt="Beskær profilbillede"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ transform: `translate(${avatarCrop.x}px, ${avatarCrop.y}px) scale(${avatarCrop.zoom})` }}
                />
              )}
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zoom</label>
              <input
                type="range"
                min="1"
                max="3"
                step="0.01"
                value={avatarCrop.zoom}
                onChange={(e) => setAvatarCrop(prev => ({ ...prev, zoom: Number(e.target.value) }))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flyt vandret</label>
              <input
                type="range"
                min="-150"
                max="150"
                step="1"
                value={avatarCrop.x}
                onChange={(e) => setAvatarCrop(prev => ({ ...prev, x: Number(e.target.value) }))}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flyt lodret</label>
              <input
                type="range"
                min="-150"
                max="150"
                step="1"
                value={avatarCrop.y}
                onChange={(e) => setAvatarCrop(prev => ({ ...prev, y: Number(e.target.value) }))}
                className="w-full"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <Button variant="outline" onClick={resetAvatarCropState} disabled={updatingAvatar}>
              Annuller
            </Button>
            <Button variant="primary" onClick={handleUploadCroppedAvatar} isLoading={updatingAvatar}>
              Gem billede
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default Settings;